import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { 
  ClientDetailsV2Request,
  ClientDetailsV2Response,
  ClientHistoryV2Request,
  ClientHistoryV2Response,
  ClientLimitsV2Request,
  ClientLimitsV2Response,
  ProcessWithdrawalRequest,
  ProcessWithdrawalResponse,
  ConfirmWithdrawalRequest,
  ConfirmWithdrawalResponse,
  ConfirmWithdrawalResponsePayload,
  GetBonusAmountRequest,
  GetBonusAmountResponse,
  GetBonusAmountResponsePayload,
  VerifyClientBankDetailsRequest,
  VerifyClientBankDetailsResponse,
} from '@/Models';
import { logApiCall, getTraceId } from '@/Services';
import { RootState } from '@/Redux/reduxStore';

const gatewayBaseUrl = import.meta.env.VITE_GATEWAY_BASE_URL;

export const gatewayApi = createApi({
  reducerPath: 'gatewayApi',
  baseQuery: fetchBaseQuery({
    baseUrl: gatewayBaseUrl,
    prepareHeaders: async (headers, { getState }) => {
      // Get user from Redux store instead of userManager
      const state = getState() as RootState;
      const user = state.auth.user;
      const premiumSourceUrl = state.iframeParams.premiumSourceUrl;

      if (user?.access_token) {
        headers.set('Authorization', `Bearer ${user.access_token}`);
      }

      if(premiumSourceUrl){
        headers.set('Referer', premiumSourceUrl);
      }

      const traceId = getTraceId();
      if (traceId) {
        headers.set('x-trace-id', traceId);
      }

      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['ClientDetailsV2', 'ClientHistory', 'ClientLimits'],
  endpoints: (builder) => ({
    getClientDetailsV2: builder.mutation<ClientDetailsV2Response, ClientDetailsV2Request>({
      query: (body) => ({
        url: 'api/v2/clientdetails',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ClientDetailsV2'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        const startTime = performance.now();
        try {
          await queryFulfilled;
          const duration = performance.now() - startTime;
          logApiCall('POST', `${gatewayBaseUrl}/api/v2/clientdetails`, 200, duration);
        } catch (error: unknown) {
          const duration = performance.now() - startTime;
          const apiError = error as { error?: { status?: number } };
          logApiCall('POST', `${gatewayBaseUrl}/api/v2/clientdetails`, apiError?.error?.status, duration, error instanceof Error ? error : new Error(String(error)));
        }
      },
    }),
    getClientHistory: builder.mutation<ClientHistoryV2Response, ClientHistoryV2Request>({
      query: (body) => ({
        url: 'api/v2/clienthistory',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ClientHistory'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        const startTime = performance.now();
        try {
          await queryFulfilled;
          const duration = performance.now() - startTime;
          logApiCall('POST', `${gatewayBaseUrl}/api/v2/clienthistory`, 200, duration);
        } catch (error: unknown) {
          const duration = performance.now() - startTime;
          const apiError = error as { error?: { status?: number } };
          logApiCall('POST', `${gatewayBaseUrl}/api/v2/clienthistory`, apiError?.error?.status, duration, error instanceof Error ? error : new Error(String(error)));
        }
      },
    }),
    processWithdrawal: builder.mutation<ProcessWithdrawalResponse, ProcessWithdrawalRequest>({
      query: (body) => ({
        url: '/GatewayApi/process-request-v2',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ClientDetailsV2', 'ClientHistory'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        const startTime = Date.now();
        try {
          await queryFulfilled;
          logApiCall('POST', `${gatewayBaseUrl}/GatewayApi/process-request-v2`, 200, Date.now() - startTime);
        } catch (error: unknown) {
          const apiError = error as { error?: { status?: number } };
          logApiCall('POST', `${gatewayBaseUrl}/GatewayApi/process-request-v2`, apiError?.error?.status, Date.now() - startTime, error instanceof Error ? error : new Error(String(error)));
        }
      },
    }),
    confirmWithdrawal: builder.mutation<ConfirmWithdrawalResponse, ConfirmWithdrawalRequest>({
      query: (body) => ({
        url: '/GatewayApi/process-request-v2',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ConfirmWithdrawalResponse) => {
        // Parse the responsePayload string into an object
        if (typeof response.responsePayload === 'string') {
          const startTime = Date.now();
          try {
            const parsed = JSON.parse(response.responsePayload) as ConfirmWithdrawalResponsePayload;
            return {
              ...response,
              responsePayload: response.responsePayload,
              parsedPayload: parsed,
            };
          } catch (error) {
            const duration = performance.now() - startTime;
            const apiError = error as { error?: { status?: number } };
            logApiCall('POST', `${gatewayBaseUrl}/api/v2/clientlimits`, apiError?.error?.status, duration, error instanceof Error ? error : new Error(String(error)));
          }
        }
        return response;
      },
      async onQueryStarted(_arg, { queryFulfilled }) {
        const startTime = Date.now();
        try {
          await queryFulfilled;
          logApiCall('POST', `${gatewayBaseUrl}/GatewayApi/process-request-v2`, 200, Date.now() - startTime);
        } catch (error: unknown) {
          const apiError = error as { error?: { status?: number } };
          logApiCall('POST', `${gatewayBaseUrl}/GatewayApi/process-request-v2`, apiError?.error?.status, Date.now() - startTime, error instanceof Error ? error : new Error(String(error)));
        }
      },
    }),
    getBonusAmount: builder.mutation<GetBonusAmountResponse, GetBonusAmountRequest>({
      query: (body) => ({
        url: '/GatewayApi/process-request-v2',
        method: 'POST',
        body,
      }),
      transformResponse: (response: GetBonusAmountResponse) => {
        // Parse the responsePayload string into an object
        if (typeof response.responsePayload === 'string') {
          const startTime = performance.now();
          try {
            const parsed = JSON.parse(response.responsePayload) as GetBonusAmountResponsePayload;
            return {
              ...response,
              responsePayload: response.responsePayload,
              parsedPayload: parsed,
            };
          } catch (error) {
            const duration = performance.now() - startTime;
            const apiError = error as { error?: { status?: number } };
            logApiCall('POST', `${gatewayBaseUrl}/api/v2/clientlimits`, apiError?.error?.status, duration, error instanceof Error ? error : new Error(String(error)));
          }
        }
        return response;
      },
      async onQueryStarted(_arg, { queryFulfilled }) {
        const startTime = Date.now();
        try {
          await queryFulfilled;
          logApiCall('POST', `${gatewayBaseUrl}/GatewayApi/process-request-v2`, 200, Date.now() - startTime);
        } catch (error: unknown) {
          const apiError = error as { error?: { status?: number } };
          logApiCall('POST', `${gatewayBaseUrl}/GatewayApi/process-request-v2`, apiError?.error?.status, Date.now() - startTime, error instanceof Error ? error : new Error(String(error)));
        }
      },
    }),
    getClientLimits: builder.mutation<ClientLimitsV2Response, ClientLimitsV2Request>({
      query: (body) => ({
        url: 'api/v2/clientlimits',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ClientLimits'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        const startTime = performance.now();
        try {
          await queryFulfilled;
          const duration = performance.now() - startTime;
          logApiCall('POST', `${gatewayBaseUrl}/api/v2/clientlimits`, 200, duration);
        } catch (error: unknown) {
          const duration = performance.now() - startTime;
          const apiError = error as { error?: { status?: number } };
          logApiCall('POST', `${gatewayBaseUrl}/api/v2/clientlimits`, apiError?.error?.status, duration, error instanceof Error ? error : new Error(String(error)));
        }
      },
    }),
    verifyClientBankDetails: builder.mutation<VerifyClientBankDetailsResponse, VerifyClientBankDetailsRequest>({
      query: (body) => {
        return {
          url: 'api/v2/VerifyBankDetails',
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['ClientDetailsV2'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        const startTime = performance.now();
        try {
          await queryFulfilled;
          const duration = performance.now() - startTime;
          logApiCall('POST', `${gatewayBaseUrl}/api/v2/VerifyBankDetails`, 200, duration);
        } catch (error: unknown) {
          const duration = performance.now() - startTime;
          const apiError = error as { error?: { status?: number; data?: any } };
          
          // Send to parent (serialize to avoid clone errors)
          if (window.parent) {
            window.parent.postMessage({
              type: 'DEBUG_LOG',
              payload: {
                label: 'GatewayApi - verifyClientBankDetails error',
                data: {
                  status: apiError?.error?.status,
                  errorMessage: apiError?.error?.data?.error || 'Unknown error',
                  data: apiError?.error?.data
                },
                timestamp: Date.now()
              }
            }, '*');
          }
          
          logApiCall('POST', `${gatewayBaseUrl}/api/v2/VerifyBankDetails`, apiError?.error?.status, duration, error instanceof Error ? error : new Error(String(error)));
        }
      },
    }),
  }),
});

export const {
  useGetClientDetailsV2Mutation,
  useGetClientHistoryMutation,
  useProcessWithdrawalMutation,
  useConfirmWithdrawalMutation,
  useGetBonusAmountMutation,
  useGetClientLimitsMutation,
  useVerifyClientBankDetailsMutation,
} = gatewayApi;